"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type DragEvent,
} from "react";
import {
  Box,
  ChevronRight,
  ClipboardPaste,
  Copy,
  FileCode,
  FileText,
  Folder,
  FolderOpen,
  FolderPlus,
  FunctionSquare,
  Layout,
  Package,
  Pencil,
  Plus,
  Scissors,
  Star,
  Trash2,
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shadcn-components/ui/collapsible";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/shadcn-components/ui/context-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shadcn-components/ui/alert-dialog";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import { cn } from "@/lib/utils";
import type {
  ProjectFileNode,
  ProjectTreeNode,
  ProjectTreeNodeKind,
} from "@/lib/custom-indicators/types";
import {
  DEFAULT_ENTRY_FILE,
  dirname,
  listChildNames,
  uniqueChildName,
} from "@/lib/custom-indicators/project-fs";

const DRAG_MIME = "application/x-vybe-indicator-path";

/** Horizontal indent added per tree depth level (VS Code-like nesting). */
const INDENT_PX = 14;

interface PendingCreate {
  parentPath: string;
  type: "file" | "folder";
}

interface Clipboard {
  path: string;
  mode: "copy" | "cut";
}

function nodeIcon(
  kind: ProjectTreeNodeKind,
  open?: boolean,
  isRoot?: boolean
) {
  if (isRoot) {
    return <Package className="h-3.5 w-3.5 shrink-0 text-primary/80" />;
  }
  switch (kind) {
    case "folder":
      return open ? (
        <FolderOpen className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      ) : (
        <Folder className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      );
    case "cs":
    case "viis":
      return <FileCode className="h-3.5 w-3.5 shrink-0 text-emerald-500/80" />;
    case "csproj":
      return <FileCode className="h-3.5 w-3.5 shrink-0 text-blue-500/80" />;
    case "md":
      return <FileText className="h-3.5 w-3.5 shrink-0 text-violet-500/80" />;
    case "canvas":
      return <Layout className="h-3.5 w-3.5 shrink-0 text-orange-500/80" />;
    case "json":
      return <Box className="h-3.5 w-3.5 shrink-0 text-amber-500/80" />;
    default:
      return <FunctionSquare className="h-3.5 w-3.5 shrink-0 text-sky-500/80" />;
  }
}

/**
 * Build a fully opaque drag-ghost element. The native default drag image is a
 * snapshot of the row which has a transparent background, so live content
 * behind the dialog (e.g. ticking watchlist quotes) bleeds through it. An
 * opaque custom image avoids that entirely.
 */
function createDragImage(label: string): HTMLElement {
  const el = document.createElement("div");
  el.textContent = label;
  el.style.cssText = [
    "position:fixed",
    "top:-1000px",
    "left:-1000px",
    "z-index:2147483647",
    "padding:2px 10px",
    "font-size:11px",
    "line-height:16px",
    "white-space:nowrap",
    "border-radius:4px",
    "border:1px solid hsl(var(--border))",
    "background:hsl(var(--popover))",
    "color:hsl(var(--popover-foreground))",
    "box-shadow:0 4px 12px rgba(0,0,0,0.35)",
  ].join(";");
  document.body.appendChild(el);
  return el;
}

function getDragSource(e: DragEvent): string | null {
  return (
    e.dataTransfer.getData(DRAG_MIME) ||
    e.dataTransfer.getData("text/plain") ||
    null
  );
}

function canDropInto(
  sourcePath: string,
  targetFolderPath: string
): boolean {
  if (!sourcePath) return false;
  if (sourcePath === targetFolderPath) return false;
  if (targetFolderPath && sourcePath.startsWith(`${targetFolderPath}/`)) {
    return false;
  }
  if (targetFolderPath.startsWith(`${sourcePath}/`)) return false;
  return true;
}

function canPasteInto(
  clipboard: Clipboard | null,
  targetFolderPath: string
): boolean {
  if (!clipboard) return false;
  const src = clipboard.path;
  // Never paste a folder into itself or one of its own descendants.
  if (targetFolderPath === src) return false;
  if (targetFolderPath.startsWith(`${src}/`)) return false;
  // Cutting into the same parent folder is a no-op.
  if (clipboard.mode === "cut" && dirname(src) === targetFolderPath) {
    return false;
  }
  return true;
}

interface NavigatorFilesSectionProps {
  tree: ProjectTreeNode | null;
  files: ProjectFileNode[];
  activeFilePath: string;
  entryFilePath: string;
  onSelectFile: (path: string) => void;
  onCreateFile: (folderPath: string, name: string) => Promise<void>;
  onCreateFolder: (folderPath: string, name: string) => Promise<void>;
  onRename: (path: string, newName: string) => Promise<void>;
  onDelete: (path: string) => Promise<void>;
  onMove: (sourcePath: string, targetFolderPath: string) => Promise<void>;
  onCopy: (sourcePath: string, targetFolderPath: string) => Promise<void>;
  onSetEntryFile: (path: string) => void;
}

function PendingCreateRow({
  depth,
  defaultName,
  onConfirm,
  onCancel,
}: {
  depth: number;
  defaultName: string;
  onConfirm: (name: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(defaultName);

  const submit = useCallback(() => {
    onConfirm(value.trim());
  }, [onConfirm, value]);

  return (
    <div
      className="flex w-full items-center gap-1 py-0.5 pr-2"
      style={{ paddingLeft: `${depth * INDENT_PX + 4}px` }}
    >
      <span className="h-3 w-3 shrink-0" aria-hidden />
      <FileCode className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => submit()}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            submit();
          }
          if (e.key === "Escape") {
            e.preventDefault();
            onCancel();
          }
        }}
        className="h-5 flex-1 px-1 text-[11px]"
        autoFocus
      />
    </div>
  );
}

function NavigatorTreeItem({
  node,
  depth = 0,
  files,
  activeFilePath,
  entryFilePath,
  pendingCreate,
  onSelectFile,
  onStartCreate,
  onConfirmCreate,
  onCancelCreate,
  onRename,
  onRequestDelete,
  onMove,
  onSetEntryFile,
  dragSourcePath,
  setDragSourcePath,
  dragOverPath,
  setDragOverPath,
  clipboard,
  onCut,
  onCopyToClipboard,
  onPasteInto,
}: {
  node: ProjectTreeNode;
  depth?: number;
  files: ProjectFileNode[];
  activeFilePath: string;
  entryFilePath: string;
  pendingCreate: PendingCreate | null;
  onSelectFile: (path: string) => void;
  onStartCreate: (parentPath: string, type: "file" | "folder") => void;
  onConfirmCreate: (
    parentPath: string,
    type: "file" | "folder",
    name: string
  ) => void;
  onCancelCreate: () => void;
  onRename: (path: string, newName: string) => Promise<void>;
  onRequestDelete: (path: string) => void;
  onMove: (sourcePath: string, targetFolderPath: string) => Promise<void>;
  onSetEntryFile: (path: string) => void;
  dragSourcePath: string | null;
  setDragSourcePath: (path: string | null) => void;
  dragOverPath: string | null;
  setDragOverPath: (path: string | null) => void;
  clipboard: Clipboard | null;
  onCut: (path: string) => void;
  onCopyToClipboard: (path: string) => void;
  onPasteInto: (targetFolderPath: string) => void;
}) {
  const isFolder = node.type === "folder";
  const isRoot = depth === 0 && node.path === "";
  const folderPath = isRoot ? "" : node.path;
  const [open, setOpen] = useState(isRoot || depth < 2);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(node.label);
  const dragImageRef = useRef<HTMLElement | null>(null);

  // A folder row is highlighted only when it is the single active drop target.
  const isDropTarget = isFolder && dragOverPath === folderPath;

  const isActive = !isFolder && node.path === activeFilePath;
  const isEntry = node.path === entryFilePath;
  const canDelete = !isRoot && node.path !== entryFilePath;
  const showPendingCreate =
    pendingCreate != null && pendingCreate.parentPath === folderPath;

  useEffect(() => {
    if (showPendingCreate) {
      setOpen(true);
    }
  }, [showPendingCreate]);

  const pendingDefaultName = showPendingCreate
    ? uniqueChildName(
        listChildNames(files, folderPath),
        pendingCreate!.type === "file" ? "untitled.cs" : "newfolder"
      )
    : "";

  const handleRenameSubmit = useCallback(async () => {
    if (!renameValue.trim() || renameValue === node.label) {
      setRenaming(false);
      return;
    }
    try {
      await onRename(node.path, renameValue.trim());
    } catch (e) {
      window.alert(e instanceof Error ? e.message : "Rename failed");
    }
    setRenaming(false);
  }, [node.path, node.label, onRename, renameValue]);

  const handleDragStart = (e: DragEvent) => {
    if (isRoot) return;
    e.stopPropagation();
    e.dataTransfer.setData(DRAG_MIME, node.path);
    e.dataTransfer.setData("text/plain", node.path);
    e.dataTransfer.effectAllowed = "move";
    const ghost = createDragImage(node.label);
    dragImageRef.current = ghost;
    e.dataTransfer.setDragImage(ghost, 12, 8);
    setDragSourcePath(node.path);
  };

  const cleanupDragImage = () => {
    if (dragImageRef.current) {
      dragImageRef.current.remove();
      dragImageRef.current = null;
    }
  };

  const handleDragEnd = (e: DragEvent) => {
    e.stopPropagation();
    cleanupDragImage();
    setDragSourcePath(null);
    setDragOverPath(null);
  };

  const tryMove = async (e: DragEvent, targetFolderPath: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverPath(null);
    const source = getDragSource(e) || dragSourcePath;
    if (!source || !canDropInto(source, targetFolderPath)) return;
    try {
      await onMove(source, targetFolderPath);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : "Move failed");
    }
    setDragSourcePath(null);
  };

  const handleFolderDragOver = (e: DragEvent) => {
    if (!isFolder) return;
    const source = dragSourcePath;
    if (!source || !canDropInto(source, folderPath)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    setDragOverPath(folderPath);
  };

  const handleFolderDrop = (e: DragEvent) => {
    void tryMove(e, folderPath);
  };

  const fileParentPath = dirname(node.path);
  const pasteTargetPath = isFolder ? folderPath : fileParentPath;
  const canPaste = canPasteInto(clipboard, pasteTargetPath);

  const handleFileDragOver = (e: DragEvent) => {
    const source = dragSourcePath;
    if (!source || !canDropInto(source, fileParentPath)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    setDragOverPath(fileParentPath);
  };

  const handleFileDrop = (e: DragEvent) => {
    void tryMove(e, fileParentPath);
  };

  const contextMenu = (
    <ContextMenuContent className="w-48">
      {isFolder ? (
        <>
          <ContextMenuItem
            onSelect={() => onStartCreate(folderPath, "file")}
          >
            <Plus className="mr-2 h-3.5 w-3.5" />
            New File
          </ContextMenuItem>
          <ContextMenuItem
            onSelect={() => onStartCreate(folderPath, "folder")}
          >
            <FolderPlus className="mr-2 h-3.5 w-3.5" />
            New Folder
          </ContextMenuItem>
        </>
      ) : null}
      {clipboard ? (
        <>
          {isFolder ? <ContextMenuSeparator /> : null}
          <ContextMenuItem
            disabled={!canPaste}
            onSelect={() => onPasteInto(pasteTargetPath)}
          >
            <ClipboardPaste className="mr-2 h-3.5 w-3.5" />
            Paste
          </ContextMenuItem>
        </>
      ) : null}
      {!isRoot ? (
        <>
          <ContextMenuSeparator />
          {!isFolder && (node.kind === "cs" || node.kind === "viis") && !isEntry ? (
            <>
              <ContextMenuItem onSelect={() => onSetEntryFile(node.path)}>
                <Star className="mr-2 h-3.5 w-3.5" />
                Set as entry file
              </ContextMenuItem>
              <ContextMenuSeparator />
            </>
          ) : null}
          <ContextMenuItem onSelect={() => onCopyToClipboard(node.path)}>
            <Copy className="mr-2 h-3.5 w-3.5" />
            Copy
          </ContextMenuItem>
          <ContextMenuItem onSelect={() => onCut(node.path)}>
            <Scissors className="mr-2 h-3.5 w-3.5" />
            Cut
          </ContextMenuItem>
          <ContextMenuSeparator />
          <ContextMenuItem
            onSelect={() => {
              setRenameValue(node.label);
              setRenaming(true);
            }}
          >
            <Pencil className="mr-2 h-3.5 w-3.5" />
            Rename
          </ContextMenuItem>
          {canDelete ? (
            <ContextMenuItem
              className="text-destructive focus:text-destructive"
              onSelect={() => onRequestDelete(node.path)}
            >
              <Trash2 className="mr-2 h-3.5 w-3.5" />
              Delete
            </ContextMenuItem>
          ) : null}
        </>
      ) : null}
    </ContextMenuContent>
  );

  if (!isFolder) {
    return (
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <button
            type="button"
            draggable
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragOver={handleFileDragOver}
            onDrop={handleFileDrop}
            className={cn(
              "flex w-full items-center gap-1 rounded-sm py-0.5 pr-2 text-left text-[11px]",
              isActive
                ? "bg-primary/15 text-primary"
                : "text-foreground/90 hover:bg-muted/60"
            )}
            style={{ paddingLeft: `${depth * INDENT_PX + 4}px` }}
            onClick={() => onSelectFile(node.path)}
          >
            <span className="h-3 w-3 shrink-0" aria-hidden />
            {nodeIcon(node.kind)}
            {renaming ? (
              <Input
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onBlur={() => void handleRenameSubmit()}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void handleRenameSubmit();
                  if (e.key === "Escape") setRenaming(false);
                }}
                className="h-5 flex-1 px-1 text-[11px]"
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <span className="truncate">
                {node.label}
                {isEntry ? (
                  <span className="ml-1 text-[9px] text-muted-foreground">
                    (entry)
                  </span>
                ) : null}
              </span>
            )}
          </button>
        </ContextMenuTrigger>
        {contextMenu}
      </ContextMenu>
    );
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div onDragOver={handleFolderDragOver} onDrop={handleFolderDrop}>
          <Collapsible open={open} onOpenChange={setOpen}>
            <CollapsibleTrigger
              type="button"
              draggable={!isRoot}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              className={cn(
                "flex w-full items-center gap-1 rounded-sm py-0.5 pr-2 text-left text-[11px] font-medium text-foreground/90",
                isDropTarget
                  ? "bg-primary/15 ring-1 ring-inset ring-primary/40"
                  : "hover:bg-muted/60"
              )}
              style={{ paddingLeft: `${depth * INDENT_PX + 4}px` }}
            >
              <ChevronRight
                className={cn(
                  "h-3 w-3 shrink-0 text-muted-foreground transition-transform",
                  open && "rotate-90"
                )}
              />
              {nodeIcon(node.kind, open, isRoot)}
              {renaming && !isRoot ? (
                <Input
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onBlur={() => void handleRenameSubmit()}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === "Enter") void handleRenameSubmit();
                    if (e.key === "Escape") setRenaming(false);
                  }}
                  className="h-5 flex-1 px-1 text-[11px]"
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <span className="truncate">{node.label}</span>
              )}
            </CollapsibleTrigger>
            <CollapsibleContent>
              {showPendingCreate ? (
                <PendingCreateRow
                  depth={depth + 1}
                  defaultName={pendingDefaultName}
                  onConfirm={(name) =>
                    onConfirmCreate(
                      folderPath,
                      pendingCreate!.type,
                      name
                    )
                  }
                  onCancel={onCancelCreate}
                />
              ) : null}
              {node.children?.map((child) => (
                <NavigatorTreeItem
                  key={child.path || child.label}
                  node={child}
                  depth={depth + 1}
                  files={files}
                  activeFilePath={activeFilePath}
                  entryFilePath={entryFilePath}
                  pendingCreate={pendingCreate}
                  onSelectFile={onSelectFile}
                  onStartCreate={onStartCreate}
                  onConfirmCreate={onConfirmCreate}
                  onCancelCreate={onCancelCreate}
                  onRename={onRename}
                  onRequestDelete={onRequestDelete}
                  onMove={onMove}
                  onSetEntryFile={onSetEntryFile}
                  dragSourcePath={dragSourcePath}
                  setDragSourcePath={setDragSourcePath}
                  dragOverPath={dragOverPath}
                  setDragOverPath={setDragOverPath}
                  clipboard={clipboard}
                  onCut={onCut}
                  onCopyToClipboard={onCopyToClipboard}
                  onPasteInto={onPasteInto}
                />
              ))}
            </CollapsibleContent>
          </Collapsible>
        </div>
      </ContextMenuTrigger>
      {contextMenu}
    </ContextMenu>
  );
}

export default function NavigatorFilesSection({
  tree,
  files,
  activeFilePath,
  entryFilePath,
  onSelectFile,
  onCreateFile,
  onCreateFolder,
  onRename,
  onDelete,
  onMove,
  onCopy,
  onSetEntryFile,
}: NavigatorFilesSectionProps) {
  const [dragSourcePath, setDragSourcePath] = useState<string | null>(null);
  const [dragOverPath, setDragOverPath] = useState<string | null>(null);
  const [clipboard, setClipboard] = useState<Clipboard | null>(null);
  const [pendingCreate, setPendingCreate] = useState<PendingCreate | null>(
    null
  );
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const handlePasteInto = useCallback(
    (targetFolderPath: string) => {
      if (!clipboard) return;
      const op =
        clipboard.mode === "cut"
          ? onMove(clipboard.path, targetFolderPath)
          : onCopy(clipboard.path, targetFolderPath);
      void op
        .catch((err) =>
          window.alert(err instanceof Error ? err.message : "Paste failed")
        )
        .finally(() => {
          if (clipboard.mode === "cut") setClipboard(null);
        });
    },
    [clipboard, onCopy, onMove]
  );

  const handleStartCreate = useCallback(
    (parentPath: string, type: "file" | "folder") => {
      setPendingCreate({ parentPath, type });
    },
    []
  );

  const handleConfirmCreate = useCallback(
    async (parentPath: string, type: "file" | "folder", name: string) => {
      if (!name.trim()) {
        setPendingCreate(null);
        return;
      }
      try {
        if (type === "file") {
          await onCreateFile(parentPath, name.trim());
        } else {
          await onCreateFolder(parentPath, name.trim());
        }
        setPendingCreate(null);
      } catch (e) {
        window.alert(e instanceof Error ? e.message : "Create failed");
      }
    },
    [onCreateFile, onCreateFolder]
  );

  const handleContainerDragOver = (e: DragEvent) => {
    e.preventDefault();
  };

  const handleContainerDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOverPath(null);
    const source = getDragSource(e) || dragSourcePath;
    if (!source || !canDropInto(source, "")) return;
    void onMove(source, "")
      .catch((err) =>
        window.alert(err instanceof Error ? err.message : "Move failed")
      )
      .finally(() => setDragSourcePath(null));
  };

  // Belt-and-suspenders: `dragend` bubbles to the container from the source
  // row, so any lingering highlight is cleared even if the drop landed
  // outside a valid target.
  const handleContainerDragEnd = () => {
    setDragOverPath(null);
    setDragSourcePath(null);
  };

  if (!tree) {
    return (
      <div className="flex flex-1 items-center justify-center p-4 text-xs text-muted-foreground">
        Loading project…
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-center gap-1 border-b border-border px-2 py-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 gap-1 px-1.5 text-[10px]"
          onClick={() => handleStartCreate("", "file")}
        >
          <Plus className="h-3 w-3" />
          File
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 gap-1 px-1.5 text-[10px]"
          onClick={() => handleStartCreate("", "folder")}
        >
          <FolderPlus className="h-3 w-3" />
          Folder
        </Button>
      </div>
      <div
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-1"
        onDragOver={handleContainerDragOver}
        onDrop={handleContainerDrop}
        onDragEnd={handleContainerDragEnd}
      >
        <NavigatorTreeItem
          node={tree}
          files={files}
          activeFilePath={activeFilePath}
          entryFilePath={entryFilePath || DEFAULT_ENTRY_FILE}
          pendingCreate={pendingCreate}
          onSelectFile={onSelectFile}
          onStartCreate={handleStartCreate}
          onConfirmCreate={handleConfirmCreate}
          onCancelCreate={() => setPendingCreate(null)}
          onRename={onRename}
          onRequestDelete={setDeleteTarget}
          onMove={onMove}
          onSetEntryFile={onSetEntryFile}
          dragSourcePath={dragSourcePath}
          setDragSourcePath={setDragSourcePath}
          dragOverPath={dragOverPath}
          setDragOverPath={setDragOverPath}
          clipboard={clipboard}
          onCut={(path) => setClipboard({ path, mode: "cut" })}
          onCopyToClipboard={(path) => setClipboard({ path, mode: "copy" })}
          onPasteInto={handlePasteInto}
        />
      </div>

      <AlertDialog
        open={deleteTarget != null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete item</AlertDialogTitle>
            <AlertDialogDescription>
              Delete &quot;{deleteTarget?.split("/").pop()}&quot;? This cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleteTarget) {
                  void onDelete(deleteTarget).catch((err) =>
                    window.alert(
                      err instanceof Error ? err.message : "Delete failed"
                    )
                  );
                }
                setDeleteTarget(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
